import React, { useState } from 'react';
import {
  Card,
  Form,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Checkbox,
  Select,
  DatePicker,
  message,
  Progress,
  Alert,
  Divider,
  Statistic,
  Tag,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  CodeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { DataType as ExportDataType, ExportFormat, DateRange } from '../types';
import { DATA_TYPES as EXPORT_DATA_TYPES, EXPORT_FORMATS } from '../constants';


const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ExportData: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedDataTypes, setSelectedDataTypes] = useState<ExportDataType[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const handleExport = async (values: any) => {
    setLoading(true);
    setProgress(0);

    try {
      // Simulate export progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      message.success('Export completed successfully! Download will start automatically.');
      
      // Reset form
      setProgress(0);
      form.resetFields();
      setSelectedDataTypes([]);
      setDateRange(null);
    } catch (error) {
      message.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFormatIcon = (format: ExportFormat) => {
    const iconMap = {
      csv: <FileTextOutlined />,
      json: <CodeOutlined />,
      excel: <FileExcelOutlined />,
      pdf: <FilePdfOutlined />,
    };
    return iconMap[format];
  };

  const getEstimatedSize = () => {
    if (selectedDataTypes.length === 0) return '0 MB';
    
    // Mock size calculation based on selected data types (in MB)
    const sizeMap: Record<string, number> = {
      users: 2,
      clients: 15,
      coaches: 5,
      programs: 25,
      exercises: 10,
      nutrition: 8,
      assessments: 12,
      analytics: 30,
    };

    const totalSize = selectedDataTypes.reduce((acc, type) => acc + (sizeMap[type] || 0), 0);
    const formatMultiplier: Record<string, number> = {
      csv: 1,
      json: 1.5,
      excel: 2,
      pdf: 3,
    };

    const estimatedSize = totalSize * (formatMultiplier[selectedFormat] || 1);
    return estimatedSize < 1000 ? `${estimatedSize.toFixed(1)} MB` : `${(estimatedSize / 1000).toFixed(1)} GB`;
  };

  const getEstimatedRecords = () => {
    if (selectedDataTypes.length === 0) return 0;
    
    // Mock record count based on selected data types
    const recordMap: Record<string, number> = {
      users: 129,
      clients: 1247,
      coaches: 48,
      programs: 156,
      exercises: 2341,
      nutrition: 4567,
      assessments: 892,
      analytics: 1834,
    };

    return selectedDataTypes.reduce((acc, type) => acc + (recordMap[type] || 0), 0);
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* Export Configuration */}
        <Col span={16}>
          <Card title="Export Configuration">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleExport}
            >
              {/* Data Types Selection */}
              <Form.Item
                label="Select Data Types to Export"
                name="dataTypes"
                rules={[{ required: true, message: 'Please select at least one data type' }]}
              >
                <div>
                  <Row gutter={[16, 16]}>
                    {EXPORT_DATA_TYPES.map(dataType => (
                      <Col span={12} key={dataType.value}>
                        <Card 
                          size="small" 
                          style={{ 
                            cursor: 'pointer',
                            border: selectedDataTypes.includes(dataType.value) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                          }}
                          onClick={() => {
                            const newSelection = selectedDataTypes.includes(dataType.value)
                              ? selectedDataTypes.filter(t => t !== dataType.value)
                              : [...selectedDataTypes, dataType.value];
                            setSelectedDataTypes(newSelection);
                            form.setFieldsValue({ dataTypes: newSelection });
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox 
                              checked={selectedDataTypes.includes(dataType.value)}
                              style={{ marginRight: 8 }}
                            />
                            <div style={{ flex: 1 }}>
                              <Text strong>{dataType.label}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Table: {dataType.table}
                              </Text>
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Form.Item>

              {/* Export Format */}
              <Form.Item
                label="Export Format"
                name="format"
                initialValue="csv"
                rules={[{ required: true, message: 'Please select export format' }]}
              >
                <Select
                  style={{ width: '100%' }}
                  onChange={setSelectedFormat}
                >
                  {EXPORT_FORMATS.map(format => (
                    <Option key={format.value} value={format.value}>
                      <Space>
                        {getFormatIcon(format.value)}
                        <div>
                          <Text>{format.label}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {format.extension}
                          </Text>
                        </div>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Date Range */}
              <Form.Item
                label="Date Range (Optional)"
                name="dateRange"
              >
                <RangePicker
                  style={{ width: '100%' }}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange({
                        start: dates[0].format('YYYY-MM-DD'),
                        end: dates[1].format('YYYY-MM-DD'),
                      });
                    } else {
                      setDateRange(null);
                    }
                  }}
                />
              </Form.Item>

              {/* Additional Options */}
              <Form.Item label="Additional Options">
                <Space direction="vertical">
                  <Checkbox>Include deleted records</Checkbox>
                  <Checkbox>Include sensitive data</Checkbox>
                  <Checkbox>Compress output file</Checkbox>
                </Space>
              </Form.Item>

              {/* Export Progress */}
              {loading && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Export Progress:</Text>
                  <Progress percent={progress} status="active" />
                </div>
              )}

              {/* Export Button */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<DownloadOutlined />}
                  size="large"
                  disabled={selectedDataTypes.length === 0}
                >
                  {loading ? 'Exporting...' : 'Start Export'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Export Summary */}
        <Col span={8}>
          <Card title="Export Summary">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Statistic
                title="Selected Data Types"
                value={selectedDataTypes.length}
                suffix={`/ ${EXPORT_DATA_TYPES.length}`}
              />
              
              <Statistic
                title="Estimated Records"
                value={getEstimatedRecords()}
                prefix={<CalendarOutlined />}
              />
              
              <Statistic
                title="Estimated File Size"
                value={getEstimatedSize()}
                prefix={<FileTextOutlined />}
              />

              <Divider />

              <div>
                <Text strong>Selected Data Types:</Text>
                <div style={{ marginTop: 8 }}>
                  {selectedDataTypes.length === 0 ? (
                    <Text type="secondary">No data types selected</Text>
                  ) : (
                    selectedDataTypes.map(type => {
                      const dataType = EXPORT_DATA_TYPES.find(dt => dt.value === type);
                      return (
                        <Tag key={type} style={{ marginBottom: 4 }}>
                          {dataType?.label}
                        </Tag>
                      );
                    })
                  )}
                </div>
              </div>

              <Divider />

              <div>
                <Text strong>Format:</Text>
                <div style={{ marginTop: 8 }}>
                  {selectedFormat && (
                    <Tag color="blue">
                      {getFormatIcon(selectedFormat)}
                      <span style={{ marginLeft: 4 }}>
                        {EXPORT_FORMATS.find(f => f.value === selectedFormat)?.label}
                      </span>
                    </Tag>
                  )}
                </div>
              </div>

              {dateRange && (
                <>
                  <Divider />
                  <div>
                    <Text strong>Date Range:</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        {dateRange.start} to {dateRange.end}
                      </Text>
                    </div>
                  </div>
                </>
              )}
            </Space>
          </Card>

          <Card title="Export Tips" style={{ marginTop: 16 }}>
            <Alert
              message="Best Practices"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 16 }}>
                  <li>CSV format is recommended for large datasets</li>
                  <li>Use date ranges to limit data size</li>
                  <li>PDF format is best for reports and summaries</li>
                  <li>JSON format preserves data structure</li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ExportData; 